import { PropTypes } from 'typed/imports';
import { ComponentEventHandler1, PureComponent } from 'typed/components/utils';
import { addEvent, removeEvent } from 'typed/utils';
import './SliderRangeInput.less';

enum DragTarget {
    START = 'START',
    END = 'END'
}

export interface SliderRangeInputValue {
    start: number;
    end: number;
}

interface Props {
    value?: SliderRangeInputValue;
    min?: number;
    max?: number;
    tickInterval?: number;
    valueFormatter?: (value: number) => string;
    onChange?: ComponentEventHandler1<SliderRangeInputValue>;
}

interface State {
    start: number;
    end: number;
}

export default class SliderRangeInput extends PureComponent<Props, State> {
    static readonly propTypes = {
        value: PropTypes.shape({
            start: PropTypes.number,
            end: PropTypes.number
        }),
        min: PropTypes.number,
        max: PropTypes.number,
        onChange: PropTypes.func
    };

    static readonly defaultProps = {
        value: {
            start: 0,
            end: 100
        },
        min: 0,
        max: 100,
        tickInterval: 1
    };

    state: State = {
        start: 0,
        end: 100
    };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        const
            { min, max } = nextProps,
            rangeLength = max! - min!,
            start = nextProps.value!.start > min! ? nextProps.value!.start : min!,
            end = nextProps.value!.end < max! ? nextProps.value!.end : max!;

        if (start !== nextProps.value!.start || end !== nextProps.value!.end) {
            this._onChange(start, end);
        }

        this.setState({
            start: (start - min!) / rangeLength * 100,
            end: (end - min!) / rangeLength * 100
        });
    }

    componentWillUnmount() {
        removeEvent(document.body, 'mousemove', this._onPointMouseMove, true);
        removeEvent(document.body, 'mouseup', this._onPointMouseUp, true);
    }

    _dragTarget: DragTarget | null = null;
    _dragResult: number | null = null;

    private _onChange = (startValue: number, endValue: number) => {
        if (this.props.onChange) {
            this.props.onChange(this, { start: startValue, end: endValue });
        }
    };

    private _onPointMouseDown = (target: DragTarget) => {
        this._dragTarget = target;

        addEvent(document.body, 'mousemove', this._onPointMouseMove, true);
        addEvent(document.body, 'mouseup', this._onPointMouseUp, true);
    };

    private _onPointMouseMove = (event: MouseEvent) => {
        const
            { axis, range, start, end } = this.refs,
            startLabel = this.refs['start-label'],
            endLabel = this.refs['end-label'];

        if (!startLabel || !endLabel || !axis || !range || !start || !end) {
            return;
        }

        const
            axisBounds = (axis as unknown as HTMLElement).getBoundingClientRect(),
            { start: startPoint, end: endPoint } = this.state,
            { min, max } = this.props,
            rangeLength = max! - min!;

        if (this._dragTarget === DragTarget.START) {
            const percentage = (event.clientX - axisBounds.left) / axisBounds.width * 100;
            this._dragResult = Math.min(Math.max(percentage, 0), endPoint!);

            (start as unknown as HTMLElement).style.left = `${this._dragResult}%`;
            (range as unknown as HTMLElement).style.left = `${this._dragResult}%`;
            (range as unknown as HTMLElement).style.width = `${endPoint! - this._dragResult}%`;

            (startLabel as unknown as HTMLElement).innerHTML = this._formatValue(min! + rangeLength * (this._dragResult / 100));
        }
        else {
            const percentage = (event.clientX - axisBounds.left) / axisBounds.width * 100;
            this._dragResult = Math.min(Math.max(percentage, startPoint!), 100);

            (end as unknown as HTMLElement).style.left = `${this._dragResult}%`;
            (range as unknown as HTMLElement).style.width = `${this._dragResult! - startPoint!}%`;

            (endLabel as unknown as HTMLElement).innerHTML = this._formatValue(min! + rangeLength * (this._dragResult / 100));
        }
    };

    private _onPointMouseUp = () => {
        removeEvent(document.body, 'mousemove', this._onPointMouseMove, true);
        removeEvent(document.body, 'mouseup', this._onPointMouseUp, true);

        const
            { start: startValue, end: endValue } = this.props.value!,
            { min, max } = this.props,
            rangeLength = max! - min!;

        if (this._dragTarget === DragTarget.START) {
            const
                newStartValue = min! + rangeLength * (this._dragResult! / 100),
                tickAdjustment = newStartValue % this.props.tickInterval!;

            this._onChange(newStartValue - tickAdjustment, endValue);
        }
        else {
            const
                newEndValue = min! + rangeLength * (this._dragResult! / 100),
                tickAdjustment = newEndValue % this.props.tickInterval!;

            this._onChange(startValue, newEndValue - tickAdjustment);
        }

        this._dragTarget = null;
        this._dragResult = null;
    };


    private _formatValue(value: number): string {
        if (this.props.valueFormatter) {
            return this.props.valueFormatter(value);
        }

        return '';
    }

    render() {
        const { start, end } = this.state;

        return (
            <div className="slider-range-input">
                <div ref="start-label" className="label start">{this._formatValue(this.props.value!.start)}</div>

                <div className="slider">
                    <div ref="axis" className="axis default" />
                    <div ref="range" className="axis selected-range" style={{ width: `${end - start}%`, left: `${start}%` }} />
                    <div ref="start" className="point" style={{ left: `${start}%` }} onMouseDown={this._onPointMouseDown.bind(this, DragTarget.START)} />
                    <div ref="end" className="point" style={{ left: `${end}%` }} onMouseDown={this._onPointMouseDown.bind(this, DragTarget.END)} />
                </div>

                <div ref="end-label" className="label end">{this._formatValue(this.props.value!.end)}</div>
            </div>
        );
    }
}
